import { motion } from 'framer-motion';
import { FiHeart, FiTarget, FiUsers, FiArrowRight, FiCheckCircle } from 'react-icons/fi';
import { useTranslation } from 'react-i18next';
import { animationVariants, motionProps, useOptimizedAnimation } from '../../hooks/useOptimizedAnimation';

const Mission = () => {
    const { t } = useTranslation();
    const { ref, controls } = useOptimizedAnimation(0.3, true);

    const missionPoints = [
        {
            icon: FiUsers,
            title: "Empowerment",
            text: t('mission.points.empower'),
        },
        {
            icon: FiTarget,
            title: "Transformation",
            text: t('mission.points.transform'),
        },
        {
            icon: FiHeart,
            title: "Participation",
            text: t('mission.points.participate'),
        },
    ];

    const impactStats = [
        { number: "3+", label: t('mission.impact') },
        { number: "50+", label: "Communities" },
        { number: "1000+", label: "Lives Changed" },
    ];

    return (
        <section id="mission" ref={ref} className="relative overflow-hidden bg-gradient-to-br from-green-50 via-white to-green-50">
            {/* Background Pattern */}
            <div className="absolute inset-0 opacity-5">
                <div className="absolute top-20 left-10 h-32 w-32 rounded-full bg-green-600"></div>
                <div className="absolute top-40 right-20 h-24 w-24 rounded-full bg-green-400"></div>
                <div className="absolute bottom-20 left-1/4 h-16 w-16 rounded-full bg-green-500"></div>
            </div>

            <div className="relative px-4 py-20 md:px-8 lg:px-16">
                <div className="mx-auto max-w-7xl">
                    {/* Header Section */}
                    <motion.div
                        className="text-center mb-16"
                        variants={animationVariants.fadeInUp}
                        initial="hidden"
                        animate={controls}
                        {...motionProps}
                    >
                        <div className="inline-flex items-center space-x-3 mb-6">
                            <div className="h-1 w-12 bg-gradient-to-r from-green-600 to-green-400"></div>
                            <span className="font-semibold tracking-wide text-green-600 uppercase text-sm">{t('mission.title')}</span>
                            <div className="h-1 w-12 bg-gradient-to-r from-green-400 to-green-600"></div>
                        </div>
                        <h2 className="text-4xl leading-tight font-bold text-gray-900 md:text-6xl mb-6">
                            {t('mission.subtitle')}
                        </h2>
                        <div className="w-24 h-1 bg-gradient-to-r from-green-600 to-green-400 mx-auto"></div>
                    </motion.div>

                    <div className="grid items-center gap-16 lg:grid-cols-2">
                        {/* Content Section */}
                        <motion.div
                            className="gpu-accelerated space-y-8"
                            variants={animationVariants.fadeInLeft}
                            initial="hidden"
                            animate={controls}
                            {...motionProps}
                        >
                            {/* Mission Statement */}
                            <div className="relative">
                                <div className="absolute -inset-4 bg-gradient-to-r from-green-600/10 to-green-400/10 rounded-2xl blur-xl"></div>
                                <div className="relative rounded-2xl bg-white/80 backdrop-blur-sm border border-green-100 p-8 shadow-xl">
                                    <div className="flex items-start space-x-4">
                                        <div className="flex-shrink-0">
                                            <div className="h-12 w-12 rounded-full bg-gradient-to-r from-green-600 to-green-400 flex items-center justify-center">
                                                <FiCheckCircle className="h-6 w-6 text-white" />
                                            </div>
                                        </div>
                                        <div>
                                            <h3 className="text-xl font-bold text-gray-900 mb-4">Our Mission Statement</h3>
                                            <p className="text-lg leading-relaxed text-gray-700">
                                                {t('mission.statement')}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Mission Points */}
                            <div className="space-y-6">
                                {missionPoints.map((point, index) => (
                                    <motion.div
                                        key={index}
                                        className="group"
                                        variants={{
                                            hidden: { opacity: 0, x: -20 },
                                            visible: { opacity: 1, x: 0 },
                                        }}
                                        initial="hidden"
                                        animate={controls}
                                        transition={{ duration: 0.4, delay: 0.2 + index * 0.1 }}
                                    >
                                        <div className="flex items-start space-x-6 p-6 rounded-xl bg-white/60 backdrop-blur-sm border border-green-100 hover:bg-white/80 hover:shadow-lg transition-all duration-300 group-hover:scale-105">
                                            <div className="flex-shrink-0">
                                                <div className="h-14 w-14 rounded-full bg-gradient-to-r from-green-600 to-green-400 flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                                                    <point.icon className="h-7 w-7 text-white" />
                                                </div>
                                            </div>
                                            <div className="flex-1">
                                                <h4 className="text-lg font-bold text-gray-900 mb-2">{point.title}</h4>
                                                <p className="leading-relaxed text-gray-700">{point.text}</p>
                                            </div>
                                            <FiArrowRight className="h-5 w-5 text-green-600 opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                                        </div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>

                        {/* Image and Stats Section */}
                        <motion.div
                            className="relative"
                            variants={{
                                hidden: { opacity: 0, x: 50 },
                                visible: { opacity: 1, x: 0 },
                            }}
                            initial="hidden"
                            animate={controls}
                            transition={{ duration: 0.6, delay: 0.3 }}
                        >
                            {/* Main Image */}
                            <div className="relative mb-8">
                                <div className="absolute -inset-6 rounded-3xl bg-gradient-to-r from-green-600/20 to-green-400/20 blur-2xl"></div>
                                <div className="relative overflow-hidden rounded-2xl border-4 border-white shadow-2xl">
                                    <img 
                                        className="h-96 w-full object-cover" 
                                        src="/images/IMG-20250814-WA0014.jpg" 
                                        alt="ACDHA Mission" 
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-green-900/60 via-transparent to-transparent"></div>
                                    
                                    {/* Overlay Content */}
                                    <div className="absolute bottom-6 left-6 right-6">
                                        <div className="bg-white/90 backdrop-blur-sm rounded-xl p-4">
                                            <h4 className="text-lg font-bold text-gray-900 mb-2">Making a Difference</h4>
                                            <p className="text-sm text-gray-700">Every action we take brings us closer to our vision of empowered communities.</p>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Impact Stats */}
                            <div className="grid grid-cols-3 gap-4">
                                {impactStats.map((stat, index) => (
                                    <motion.div
                                        key={index}
                                        className="text-center p-4 rounded-xl bg-white/80 backdrop-blur-sm border border-green-100 hover:bg-white hover:shadow-lg transition-all duration-300"
                                        initial={{ opacity: 0, y: 20 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        transition={{ duration: 0.4, delay: 0.5 + index * 0.1 }}
                                        whileHover={{ scale: 1.05 }}
                                    >
                                        <div className="text-2xl font-bold text-green-600 mb-1">{stat.number}</div>
                                        <div className="text-xs text-gray-600 font-medium">{stat.label}</div>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default Mission;
